import { type ReactNode, useLayoutEffect, useRef, useState } from 'react'

import { Button, type ButtonVariant } from '@/components/button'
import { FormError } from '@/components/form-error'
import { cx } from '@/lib/cx'
import { useT } from '@/lib/i18n/translate'

export interface ConfirmButtonProps {
  label: string
  /** Names the target on the trigger, e.g. "Delete: Phở bò". */
  ariaLabel?: string | undefined
  variant?: ButtonVariant
  size?: 'sm' | 'md'
  disabled?: boolean
  confirmLabel: string
  /** The confirming press's look; "danger" by default. */
  confirmVariant?: ButtonVariant
  /** Names the target on the confirming press. */
  confirmAriaLabel?: string | undefined
  /** The way out; "Cancel" by default. */
  keepLabel?: string
  /** The edge the trigger sits on in a stretched box; once pressed, its measured position decides. */
  align?: 'start' | 'end'
  /** With one, the first press shows it instead of arming. */
  refusal?: string | undefined
  /** Sits above the pair while armed. */
  note?: ReactNode
  /** The second press; the pair goes away once it settles, whichever way. */
  confirm: () => Promise<unknown>
  /** The action's failure, drawn under the pair on the same edge. */
  error?: unknown
}

/**
 * A destructive action behind a second press (PRD: keep destructive actions apart). Arming
 * moves focus to the keep button, so Enter and a repeated touch both keep.
 */
export function ConfirmButton({
  label,
  ariaLabel,
  variant = 'ghost',
  size = 'sm',
  disabled,
  confirmLabel,
  confirmVariant = 'danger',
  confirmAriaLabel,
  keepLabel,
  align = 'start',
  refusal,
  note,
  confirm,
  error,
}: ConfirmButtonProps) {
  const t = useT()
  const [confirming, setConfirming] = useState(false)
  const [pending, setPending] = useState(false)
  const [pressed, setPressed] = useState(false)
  // The keep button takes the trigger's spot, so a second touch is harmless: it goes on the
  // trigger's edge and covers its width, with the confirm out toward the open side.
  const trigger = useRef<HTMLButtonElement>(null)
  const keep = useRef<HTMLButtonElement>(null)
  const [side, setSide] = useState(align)
  const [spot, setSpot] = useState<DOMRect>()
  // Derived, so the alert goes away once the blocker does.
  const blocked = pressed && refusal !== undefined

  function press() {
    const rect = trigger.current?.getBoundingClientRect()
    if (rect) {
      const room = document.documentElement.clientWidth - rect.right
      setSide(rect.left > room ? 'end' : rect.left < room ? 'start' : align)
      setSpot(rect)
    }
    setPressed(refusal !== undefined)
    if (refusal === undefined) setConfirming(true)
  }

  // The guess can miss when the box anchors the other edge: then the keep sits beside the spot, so flip.
  useLayoutEffect(() => {
    if (!confirming || !spot?.width) return
    const rect = keep.current?.getBoundingClientRect()
    if (rect?.width && (rect.right <= spot.left || rect.left >= spot.right)) {
      setSide((current) => (current === 'end' ? 'start' : 'end'))
    }
  }, [confirming, spot])

  async function run() {
    setPending(true)
    try {
      await confirm()
    } catch {
      // The caller's mutation holds the error; the pair just closes.
    } finally {
      setPending(false)
      setConfirming(false)
    }
  }

  return (
    <span className={cx('flex flex-col gap-2', side === 'end' ? 'items-end' : 'items-start')}>
      {confirming ? (
        <>
          {note}
          <span className={cx('flex items-center gap-2', side === 'end' && 'flex-row-reverse')}>
            <Button
              ref={keep}
              size={size}
              variant="ghost"
              autoFocus
              disabled={pending}
              style={{ minWidth: spot?.width || undefined }}
              onClick={() => setConfirming(false)}
            >
              {keepLabel ?? t('common.cancel')}
            </Button>
            <Button
              size={size}
              variant={confirmVariant}
              disabled={disabled}
              loading={pending}
              aria-label={confirmAriaLabel}
              onClick={() => void run()}
            >
              {confirmLabel}
            </Button>
          </span>
        </>
      ) : (
        <Button ref={trigger} size={size} variant={variant} disabled={disabled} aria-label={ariaLabel} onClick={press}>
          {label}
        </Button>
      )}
      {blocked && (
        <span role="alert" className="text-danger text-sm">
          {refusal}
        </span>
      )}
      <FormError error={error} />
    </span>
  )
}
